import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card } from '@safetyhub/ui';
import Modal from '../Modal';
// 조직 처분 어휘는 server·admin과 같은 enum 하나를 본다 (shared/types/org-withdrawal-action.ts)
import { OrgWithdrawalAction } from '@safetyhub/shared';
import {
  usersApi,
  dataExportApi,
  extractApiError,
  OrgActionConfig,
  DownloadMethod,
  ExportTargetsResponse,
  ExportStatusResponse,
} from '../../lib/users';
import { useAuthStore } from '../../stores/auth';
import { Membership } from '../../lib/auth';
import { isOwnedActiveOrganization } from '../../lib/membershipAccess';
import { OWNER_LABEL } from '../../lib/organizations';

/** 문구가 여러 곳에서 「조직소유자」를 인용한다 — 옛 `ROLE_LABELS.OWNER`와 같은 값(P7-b-2) */
const ROLE_LABELS = { OWNER: OWNER_LABEL } as const;

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * `confirm-no-export`는 **내보낼 자료가 하나도 없는 사람** 전용 최종 확인이다.
 *
 * 그 경우 1단계(`request` — 내보내기 방식 선택)는 고를 것이 없어 보여줄 이유가 없다.
 * 다만 비밀번호 확인은 `request` 단계에만 있던 유일한 신원 확인이라(탈퇴 확인 API는 비밀번호를
 * 받지 않는다) 이 화면이 그 자리를 대신한다 — 없애면 탈퇴에서 비밀번호 게이트가 통째로 사라진다.
 */
type Step =
  | 'org-action'
  | 'request'
  | 'processing'
  | 'confirm'
  | 'discard-confirm'
  | 'confirm-no-export';

/**
 * 폼이 있는 단계의 `id` — 푸터의 제출 버튼이 `form` 속성으로 가리킨다. base Modal이 본문과
 * 푸터를 형제 div로 렌더하므로 이 연결 없이는 `type="submit"`이 아무 폼에도 속하지 않는다.
 * 나머지 단계는 폼이 없어(버튼 onClick만) 여기 없다.
 */
const FORM_ID: Record<'request' | 'confirm-no-export', string> = {
  request: 'delete-account-request-form',
  'confirm-no-export': 'delete-account-no-export-form',
};

export default function DeleteAccountModal({
  isOpen,
  onClose,
  onSuccess,
}: DeleteAccountModalProps) {
  const { user } = useAuthStore();

  // Step 관리
  const [step, setStep] = useState<Step>('request');

  // Step 1: 요청 단계
  const [password, setPassword] = useState('');
  const [downloadMethod, setDownloadMethod] = useState<DownloadMethod>('direct');
  const [orgActions, setOrgActions] = useState<Record<string, OrgActionConfig>>({});

  // 수신처는 **계정에 등록된 것만**이다 (S27 수신자 축, 2026-08-22 사용자 확정).
  // 다운로드 링크는 로그인 없이 열리는 bearer 토큰이고, 폐쇄 케이스의 ZIP은 본인 자료가
  // 아니라 회사 전체 자료(직원 PII 포함)라 수신자가 틀리면 채널을 막아도 소용이 없다.
  // 서버도 body의 phone/email을 400으로 거부한다 — 여기서 입력을 없애는 것과 짝이다.
  const accountPhone = user?.phone ?? null;
  const accountEmail = user?.email ?? null;

  // 내보내기 대상 정보
  const [exportTargets, setExportTargets] = useState<ExportTargetsResponse | null>(null);
  const [targetsLoading, setTargetsLoading] = useState(false);
  /** 조회 실패 — null(아직 안 옴)과 구분해야 거짓 안내를 막을 수 있다 */
  const [targetsFailed, setTargetsFailed] = useState(false);

  // Step 2: 처리/확인 단계
  const [exportId, setExportId] = useState<string | null>(null);
  const [exportStatus, setExportStatus] = useState<ExportStatusResponse | null>(null);
  /**
   * 폴링 타이머 — **ref로 잡는다.**
   *
   * 예전엔 state였는데, effect의 cleanup이 **자기 렌더 시점의 값**(항상 null)을 클로저로
   * 잡아 실제 타이머를 지우지 못했다. `setPollingInterval`은 effect deps에 없어 재실행도
   * 없다. 그래서 '데이터 준비 중' 상태에서 페이지가 언마운트되면 인터벌이 살아남아 무한
   * 폴링 + 언마운트 후 setState가 됐다(2026-08-22 리뷰).
   */
  const pollRef = useRef<number | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // 공통 상태
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // 내가 소유자인 멤버십 (삭제 예정이 아닌 조직만) — 축·조직 유형 판단 근거는 그 함수 doc.
  // P6-b: 옛 `role === 'OWNER'` → 소유자 **성질**. 짝이 되는 `soleOwnerOrganizations`도
  // 서버가 같은 성질 축으로 뽑는다.
  const ownedOrgs: Membership[] =
    user?.memberships.filter(isOwnedActiveOrganization) || [];

  // 내보낼 자료가 있는가 — 1단계(내보내기 방식 선택)를 보여줄지 가르는 축.
  // 서버의 `createExport` 진입 조건·`confirmWithdrawal`의 폐기 확인 면제와 같은 술어다.
  const canExport = exportTargets?.canExport ?? false;
  /** 1단계를 건너뛸 때 대신 갈 곳 — 분기가 여러 곳이라 한 군데로 모은다. */
  const stepAfterOrgAction: Step = canExport ? 'request' : 'confirm-no-export';

  // 유일 소유자인 조직 (exportTargets에서 가져옴 — 서버가 폐쇄 판정과 같은 축으로 뽑는다)
  const soleOwnerOrgs = exportTargets?.soleOwnerOrganizations || [];
  const hasSoleOwnerOrg = soleOwnerOrgs.length > 0;
  // 폐쇄로 **다른 사람의 접근이 끊기는** 조직이 있는가 — 문구를 가르는 축이다.
  // 1인 작업공간뿐이면 "멤버들의 접근이 끊깁니다"도 "다른 분께 넘기시려면"도 거짓이 된다.
  const hasAffectedMembers = soleOwnerOrgs.some(
    (org) => (org.otherMembers?.length || 0) > 0
  );

  // Owner이지만 유일 소유자가 아닌 조직 (다른 Owner가 있음 - 자동으로 멤버십만 해제됨)
  const nonSoleOwnerOrgs = ownedOrgs.filter(
    (m) => !soleOwnerOrgs.some((so) => so.id === m.companyId)
  );

  // 내보내기 대상 조회
  const fetchExportTargets = useCallback(async () => {
    setTargetsLoading(true);
    try {
      const targets = await dataExportApi.getExportTargets();
      setExportTargets(targets);
      setTargetsFailed(false);
    } catch (err) {
      // ⚠️ 예전엔 `console.error`만 하고 넘어갔다. 그러면 `exportTargets`가 null로 남아
      // step 결정 effect(`if (exportTargets)`)가 아예 안 돌고, 화면은 **빈 목록 위에**
      // "아래 조직의 유일한 OWNER입니다 / 폐쇄됩니다"라는 거짓 안내를 그대로 보여준다.
      // 그대로 진행하면 `canExport=false` 경로로 빠져 폐기(discard) 요청이 되고, 서버가
      // `USER_WITHDRAWAL_DISCARD_CONFIRMATION_REQUIRED`로 막는데 그 코드는 재시도 복구
      // 목록에 없어 **막다른 길**이 된다(2026-08-22 리뷰).
      console.error('Failed to fetch export targets:', err);
      setTargetsFailed(true);
    } finally {
      setTargetsLoading(false);
    }
  }, []);

  // 모달 열릴 때 초기화
  useEffect(() => {
    if (isOpen) {
      setStep('org-action'); // 초기에는 org-action, exportTargets 로드 후 결정
      setPassword('');
      setDownloadMethod('direct');
      setOrgActions({});
      setExportId(null);
      setExportStatus(null);
      setError('');
      setTargetsFailed(false);
      fetchExportTargets();
    } else {
      // 모달 닫힐 때 폴링 중지
      stopPolling();
    }
  }, [isOpen, fetchExportTargets, stopPolling]);

  // 언마운트 시에도 반드시 멈춘다 — 모달을 닫지 않고 페이지를 벗어나는 경로가 있다.
  useEffect(() => stopPolling, [stopPolling]);

  // exportTargets 로드 후 orgActions 초기값 설정 및 step 결정
  useEffect(() => {
    if (exportTargets) {
      const soleOrgs = exportTargets.soleOwnerOrganizations || [];

      if (soleOrgs.length > 0) {
        // 유일 소유자 조직이 있으면 org-action(=폐쇄 확인) 단계 유지.
        // 처분은 폐쇄뿐이다 — 소유권 이전은 탈퇴와 분리했다(결정 기록 2026-08-20 정책 10).
        const initialActions: Record<string, OrgActionConfig> = {};
        soleOrgs.forEach((org) => {
          initialActions[org.id] = { action: OrgWithdrawalAction.CLOSE };
        });
        setOrgActions(initialActions);
        // step이 org-action이면 유지 (이미 다른 단계면 변경 안 함)
        if (step === 'org-action') {
          // 유지
        }
      } else {
        // 유일 소유자 조직이 없으면 org-action을 건너뛴다.
        // 내보낼 자료까지 없으면 1단계도 건너뛰고 최종 확인만 받는다.
        if (step === 'org-action') {
          setStep(exportTargets.canExport ? 'request' : 'confirm-no-export');
        }
      }
    }
  }, [exportTargets]);

  // 내보내기 상태 폴링
  useEffect(() => {
    if (step === 'processing' && exportId && pollRef.current === null) {
      pollRef.current = window.setInterval(async () => {
        try {
          const status = await dataExportApi.getExportStatus(exportId);
          setExportStatus(status);

          // ⚠️ GENERATING이 아니면 무조건 멈춘다. 예전엔 READY·FAILED만 처리해
          // EXPIRED/DELETED/DOWNLOADED가 오면 2초 폴링이 영원히 돌고 화면은 '준비 중'에
          // 멈춘 채 아무 설명이 없었다.
          if (status.status === 'GENERATING') return;

          stopPolling();

          if (status.status === 'READY' || status.status === 'DOWNLOADED') {
            setStep('confirm');
          } else if (status.status === 'FAILED') {
            setError('데이터 내보내기 생성에 실패했습니다. 다시 시도해주세요.');
            setStep('request');
          } else {
            setError('내보내기가 만료되었습니다. 다시 요청해주세요.');
            setStep('request');
          }
        } catch (err) {
          console.error('Failed to poll export status:', err);
        }
      }, 2000);
    }

    return stopPolling;
  }, [step, exportId, stopPolling]);

  // 조직 구성 변화(S51 레이스 등) 뒤의 재시도 복구.
  //
  // 서버가 처분 재확인을 요구하면(409 구성 변경 / 400 처분 미지정) 이 모달이 들고 있는
  // exportTargets·orgActions·hasSoleOwnerOrg가 낡은 것이다 — 모달을 연 시점 기준이라
  // 그 사이 유일 OWNER가 된 조직을 모른다. 그대로 두면 사용자가 같은 stale body로
  // 재시도해 계속 같은 에러를 맞는 막다른 길이 된다. 대상을 다시 읽고 org-action 단계로
  // 되돌려, 새 구성 기준의 폐쇄 확인을 다시 밟게 한다(orgActions 초기값은 exportTargets
  // 로드 effect가 새 목록으로 다시 채운다).
  const recoverIfStaleOrgState = (code: string) => {
    const staleOrgStateCodes = [
      'WITHDRAWAL_ORG_STATE_CHANGED',
      'USER_DELETE_ORG_ACTION_MISSING',
      'USER_DELETE_BUSINESS_ORG_ACTION_REQUIRED',
    ];
    if (!staleOrgStateCodes.includes(code)) return;
    setStep('org-action');
    fetchExportTargets();
  };

  // Step 1: 탈퇴 요청 제출
  const handleRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!password) {
      setError('비밀번호를 입력해주세요.');
      return;
    }

    setIsLoading(true);
    try {
      // 요청 데이터 구성 (필요한 필드만 포함)
      const requestData: Parameters<typeof usersApi.withdrawalRequest>[0] = {
        password,
        downloadMethod,
      };
      if (hasSoleOwnerOrg) {
        requestData.orgActions = orgActions;
      }
      const result = await usersApi.withdrawalRequest(requestData);

      setExportId(result.exportId);

      if (result.requiresDiscardConfirmation) {
        // 폐기 선택 시 확인 화면으로
        setStep('discard-confirm');
      } else if (result.canProceedImmediately) {
        // SMS/Email 선택 시 바로 확인 화면으로
        setStep('confirm');
      } else {
        // Direct 선택 시 처리 대기 화면으로
        setStep('processing');
      }
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      recoverIfStaleOrgState(apiError.code);
    } finally {
      setIsLoading(false);
    }
  };

  // 탈퇴 확인 제출
  const handleConfirmSubmit = async () => {
    setIsLoading(true);
    setError('');

    try {
      await usersApi.withdrawalConfirm({
        exportId: exportId || undefined,
        discardConfirmed: step === 'discard-confirm' ? true : undefined,
        orgActions: hasSoleOwnerOrg ? orgActions : undefined,
      });
      onSuccess();
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      recoverIfStaleOrgState(apiError.code);
    } finally {
      setIsLoading(false);
    }
  };

  // 내보낼 자료가 없는 경우의 탈퇴 제출 (1단계 화면 없이 한 번에)
  //
  // 요청(1단계)을 그래도 호출하는 이유는 **비밀번호 확인**이 거기에만 있기 때문이다.
  // `downloadMethod: 'discard'`는 아무것도 만들지 않고 검증만 하고 돌아온다.
  // 폐기 확인(`discardConfirmed`)은 **보내지 않는다** — 폐기할 자료가 없으므로 서버가
  // 면제하고, 억지로 true를 보내면 감사 로그에 "폐기를 확인했다"는 거짓이 남는다.
  const handleNoExportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!password) {
      setError('비밀번호를 입력해주세요.');
      return;
    }

    setIsLoading(true);
    try {
      const requestData: Parameters<typeof usersApi.withdrawalRequest>[0] = {
        password,
        downloadMethod: 'discard',
      };
      if (hasSoleOwnerOrg) {
        requestData.orgActions = orgActions;
      }
      await usersApi.withdrawalRequest(requestData);

      await usersApi.withdrawalConfirm({
        orgActions: hasSoleOwnerOrg ? orgActions : undefined,
      });
      onSuccess();
    } catch (err) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      recoverIfStaleOrgState(apiError.code);
    } finally {
      setIsLoading(false);
    }
  };

  // 다운로드
  const handleDownload = async () => {
    if (exportId) {
      try {
        await dataExportApi.downloadExport(exportId);
      } catch (err) {
        console.error('Download failed:', err);
        setError('다운로드에 실패했습니다. 다시 시도해주세요.');
      }
    }
  };

  // 내보내기 대상 텍스트 생성 (orgActions에 따라 동적)
  const getExportTargetItems = () => {
    if (!exportTargets) return [];

    const items: { text: string; included: boolean }[] = [];

    // 개인 작업공간은 **가지고 있을 때만** 포함한다.
    // 예전엔 서버가 `personalWorkspace: true`를 하드코딩해, 작업공간이 없는 사람에게도
    // 이 줄이 "내 작업공간의 모든 데이터"라고 적혀 있었다(그 사람들은 애초에 이 화면에
    // 도달하지도 못했다 — 1단계 요청이 404였다).
    // 단 아래 목록이 그 작업공간을 **이름으로** 이미 말하면 같은 것을 두 번 적지 않는다.
    const workspaceNamedBelow = exportTargets.soleOwnerOrganizations.some(
      (org) => org.isPersonalWorkspace
    );
    if (exportTargets.personalWorkspace && !workspaceNamedBelow) {
      items.push({ text: '내 작업공간의 모든 데이터', included: true });
    }

    // 유일 소유자 조직은 폐쇄되므로 항상 내보내기 대상
    exportTargets.soleOwnerOrganizations.forEach((org) => {
      items.push({ text: `${org.name} 데이터 (폐쇄 예정)`, included: true });
    });

    return items;
  };

  // Step 0: 조직 폐쇄 확인 단계 (유일 소유자 조직이 있는 경우만)
  const renderOrgActionStep = () => (
    <div className="space-y-4">
      {/* 경고 메시지 — 유일 소유자 조직은 예외 없이 폐쇄되므로 항상 표시 */}
      <div className="p-4 bg-rose-50 border border-rose-200 rounded-card">
        <div className="flex items-start">
          <svg
            className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-rose-800">주의</h3>
            <div className="mt-1 text-sm text-rose-700 space-y-1">
              <p>회원 탈퇴 시 모든 조직 멤버십이 해제됩니다.</p>
              <p>
                탈퇴 후 <strong>30일 이내</strong>에 동일한 계정으로 로그인하면
                계정을 복구할 수 있습니다.
              </p>
              <p>30일이 지나면 계정과 데이터가 완전히 삭제됩니다.</p>
            </div>
          </div>
        </div>
      </div>

      {/* 유일 소유자 조직 — 폐쇄 안내 (선택지가 아니라 통보다) */}
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-card">
        <h4 className="text-sm font-medium text-amber-800 mb-2">
          유일 소유자 조직은 폐쇄됩니다
        </h4>
        <p className="text-sm text-amber-700 mb-3">
          아래 조직의 유일한 {ROLE_LABELS.OWNER}입니다. 탈퇴하면 조직이 폐쇄됩니다
          {hasAffectedMembers ? ' — 멤버들의 접근도 함께 끊깁니다.' : '.'}
        </p>
        <div className="space-y-2">
          {soleOwnerOrgs.map((org) => (
            <div
              key={org.id}
              className="flex items-center justify-between p-3 bg-white rounded-card border border-amber-200"
            >
              <span className="text-sm font-medium text-ink-700">
                {org.name}
                {org.isPersonalWorkspace && (
                  <span className="ml-2 text-xs font-normal text-ink-500">
                    개인/팀 작업공간
                  </span>
                )}
              </span>
              <span className="text-xs text-ink-500">
                멤버 {org.otherMembers?.length || 0}명 · 폐쇄 예정
              </span>
            </div>
          ))}
        </div>
        {/* 막다른 길이 되지 않도록 — 넘기고 싶은 사람에게 실제로 갈 곳을 준다.
            멤버가 없는 1인 작업공간에도 그대로 보여준다: 조직 밖 사람을 OWNER로 초대해
            수락받는 경로가 있고, 쌓인 점검·보고서를 후임에게 넘기려면 그 길뿐이다. */}
        <div className="mt-3 p-3 bg-white rounded-card border border-amber-200">
          <p className="text-sm text-ink-700">
            조직을 다른 분께 넘기시려면 <strong>먼저 소유권을 이전한 뒤</strong> 탈퇴해 주세요.
          </p>
          <p className="mt-1 text-xs text-ink-500">
            멤버 관리에서 다른 멤버를 {ROLE_LABELS.OWNER}로 지정하거나, 조직 밖의 사람은
            {' '}{ROLE_LABELS.OWNER}로 초대해 수락받으면 됩니다. 이전이 끝나면 탈퇴 시 이 안내가
            나타나지 않습니다.
          </p>
          <Link
            to="/members"
            onClick={onClose}
            className="mt-2 inline-block text-sm font-medium text-brand-600 hover:text-brand-700 underline"
          >
            멤버 관리로 이동
          </Link>
        </div>
        <div className="mt-3 text-xs">
          <p className="text-amber-700">
            • <strong>폐쇄</strong>: 조직 삭제, 데이터 내보내기에 포함
          </p>
        </div>
      </div>

      {/* 다른 조직소유자가 있는 조직 안내 */}
      {nonSoleOwnerOrgs.length > 0 && (
        <Card padding="sm" surface="soft" elevation="none">
          <p className="text-sm text-ink-600">
            <span className="font-medium">
              {nonSoleOwnerOrgs.map((o) => o.company.name).join(', ')}
            </span>
            : 다른 {ROLE_LABELS.OWNER}가 있어 탈퇴 시 자동으로 멤버십만 해제됩니다.
          </p>
        </Card>
      )}

      {/* ⚠️ 조회 실패를 삼키면 위 안내가 **빈 목록 위의 거짓말**이 된다. 진행도 막는다 —
          이 상태로 넘어가면 폐기(discard) 경로로 빠져 서버가 막고, 그 에러는 재시도 복구
          목록에 없어 막다른 길이 된다. */}
      {targetsFailed && (
        <div className="p-3 bg-rose-50 text-rose-700 text-sm rounded-card">
          <p className="mb-2">
            조직·자료 정보를 불러오지 못했습니다. 이 상태로는 무엇이 폐쇄되고 어떤 자료를
            받을 수 있는지 정확히 알려드릴 수 없어 진행할 수 없습니다.
          </p>
          <Button variant="secondary" onClick={fetchExportTargets} disabled={targetsLoading}>
            {targetsLoading ? '확인 중…' : '다시 확인'}
          </Button>
        </div>
      )}

      {error && (
        <div className="p-3 bg-rose-50 text-rose-600 text-sm rounded-card">{error}</div>
      )}

    </div>
  );

  // Step 1: 요청 화면
  const renderRequestStep = () => (
    <form id={FORM_ID.request} onSubmit={handleRequestSubmit}>
      <div className="space-y-4">
        {/* 경고 메시지 (유일 소유자 조직이 없는 경우만 - 있으면 org-action에서 이미 봄) */}
        {!hasSoleOwnerOrg && (
          <div className="p-4 bg-rose-50 border border-rose-200 rounded-card">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-rose-800">주의</h3>
                <div className="mt-1 text-sm text-rose-700 space-y-1">
                  <p>회원 탈퇴 시 모든 조직 멤버십이 해제됩니다.</p>
                  <p>
                    탈퇴 후 <strong>30일 이내</strong>에 동일한 계정으로 로그인하면
                    계정을 복구할 수 있습니다.
                  </p>
                  <p>30일이 지나면 계정과 데이터가 완전히 삭제됩니다.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/*
          이메일이 없으면 30일 복원이 **사실상 없는 권리**가 된다 (S27, 2026-08-22).
          복원 링크는 탈퇴 통지에 실려 가는데, 그 통지를 SMS로만 받은 사람이 비밀번호를
          잊으면 되돌릴 방법이 없다 — 탈퇴 계정의 비밀번호 재설정은 이메일 경로만 열려 있다
          (SMS는 번호 재배정 탈취 때문에 의도적으로 막혀 있다). 그래서 탈퇴를 **막지는 않되**
          지금이 등록할 마지막 시점이라는 것을 알린다.
        */}
        {!accountEmail && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-card">
            <h3 className="text-sm font-medium text-amber-900">
              등록된 이메일이 없습니다
            </h3>
            <div className="mt-1 text-sm text-amber-800 space-y-1">
              <p>탈퇴 안내와 계정 복원 링크가 문자로만 발송됩니다.</p>
              <p>
                문자를 잃어버린 뒤 비밀번호까지 잊으면 <strong>30일 복원을 쓸 수 없습니다</strong> —
                탈퇴한 계정의 비밀번호 재설정은 이메일로만 가능합니다.
              </p>
              <p>
                <Link to="/settings/account" className="underline font-medium">
                  프로필에서 이메일을 먼저 등록
                </Link>
                하시는 것을 권합니다.
              </p>
            </div>
          </div>
        )}

        {/* 다른 조직소유자가 있는 조직 안내 (유일 소유자 조직이 없는 경우만) */}
        {!hasSoleOwnerOrg && nonSoleOwnerOrgs.length > 0 && (
          <Card padding="sm" surface="soft" elevation="none">
            <p className="text-sm text-ink-600">
              <span className="font-medium">
                {nonSoleOwnerOrgs.map((o) => o.company.name).join(', ')}
              </span>
              : 다른 {ROLE_LABELS.OWNER}가 있어 탈퇴 시 자동으로 멤버십만 해제됩니다.
            </p>
          </Card>
        )}

        {/* 데이터 다운로드 방법 */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-card">
          <h4 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            데이터 다운로드 방법
          </h4>

          {/* 내보내기 대상 안내 */}
          {targetsLoading ? (
            <p className="text-sm text-blue-600 mb-3">내보내기 대상 확인 중...</p>
          ) : exportTargets && (
            <div className="mb-3 text-sm">
              <p className="text-blue-700 font-medium mb-1">내보내기 대상:</p>
              <ul className="space-y-0.5">
                {getExportTargetItems().map((item, idx) => (
                  <li key={idx} className={item.included ? 'text-blue-600' : 'text-ink-400 line-through'}>
                    • {item.text}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-2">
            <label className="flex items-start p-2 rounded hover:bg-blue-100 cursor-pointer">
              <input
                type="radio"
                name="downloadMethod"
                value="direct"
                checked={downloadMethod === 'direct'}
                onChange={() => setDownloadMethod('direct')}
                className="mt-0.5 text-brand-600"
              />
              <div className="ml-2">
                <span className="text-sm font-medium text-ink-700">직접 다운로드</span>
                <p className="text-xs text-ink-500">ZIP 파일 생성 후 바로 다운로드</p>
              </div>
            </label>

            <div className="p-2 rounded hover:bg-blue-100">
              <label className="flex items-start cursor-pointer">
                <input
                  type="radio"
                  name="downloadMethod"
                  value="sms"
                  checked={downloadMethod === 'sms'}
                  onChange={() => setDownloadMethod('sms')}
                  disabled={!accountPhone}
                  className="mt-0.5 text-brand-600 disabled:opacity-40"
                />
                <div className="ml-2 flex-1">
                  <span className="text-sm font-medium text-ink-700">SMS로 링크 받기</span>
                  <p className="text-xs text-ink-500 mb-1">
                    {accountPhone
                      ? `등록된 번호(${accountPhone})로 발송`
                      : '등록된 휴대폰 번호가 없어 선택할 수 없습니다'}
                  </p>
                </div>
              </label>
            </div>

            <div className="p-2 rounded hover:bg-blue-100">
              <label className="flex items-start cursor-pointer">
                <input
                  type="radio"
                  name="downloadMethod"
                  value="email"
                  checked={downloadMethod === 'email'}
                  onChange={() => setDownloadMethod('email')}
                  disabled={!accountEmail}
                  className="mt-0.5 text-brand-600 disabled:opacity-40"
                />
                <div className="ml-2 flex-1">
                  <span className="text-sm font-medium text-ink-700">이메일로 링크 받기</span>
                  <p className="text-xs text-ink-500 mb-1">
                    {accountEmail
                      ? `등록된 주소(${accountEmail})로 발송`
                      : '등록된 이메일이 없어 선택할 수 없습니다'}
                  </p>
                </div>
              </label>
            </div>

            <label className="flex items-start p-2 rounded hover:bg-ink-100 cursor-pointer">
              <input
                type="radio"
                name="downloadMethod"
                value="discard"
                checked={downloadMethod === 'discard'}
                onChange={() => setDownloadMethod('discard')}
                className="mt-0.5 text-ink-600"
              />
              <div className="ml-2">
                <span className="text-sm font-medium text-ink-500">다운로드 하지 않음 (폐기)</span>
                <p className="text-xs text-ink-400">데이터를 다운로드하지 않고 탈퇴</p>
              </div>
            </label>
          </div>
        </div>

        {/* 비밀번호 확인 */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-ink-700">
            비밀번호 확인
          </label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            // 탈퇴 확인 컨텍스트라 포커스 강조만 rose — 치수·굵기는 FORM_CONTROL과 동일(프로필 해제 확인과 같은 결)
            className="mt-1 block w-full h-[42px] rounded-[10px] border border-rose-200 bg-white px-3 text-[14px] font-semibold text-ink-900 placeholder:text-ink-400 placeholder:font-medium focus:outline-none focus:border-rose-500 focus:ring-[3px] focus:ring-rose-100"
            placeholder="현재 비밀번호 입력"
            autoComplete="current-password"
          />
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="p-3 bg-rose-50 text-rose-600 text-sm rounded-card">{error}</div>
        )}
      </div>

    </form>
  );

  // Step 2a: 처리 중 화면 (Direct 선택 시)
  const renderProcessingStep = () => (
    <div className="space-y-4">
      <div className="flex flex-col items-center justify-center py-8">
        {/* 로딩 스피너 */}
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mb-4"></div>
        <h3 className="text-lg font-medium text-ink-900">데이터 내보내기 준비 중</h3>
        <p className="text-sm text-ink-500 mt-2">잠시만 기다려주세요...</p>

        {exportStatus && (
          <div className="mt-4 text-sm text-ink-600">
            <p>점검: {exportStatus.inspectionCount}건</p>
            <p>보고서: {exportStatus.reportCount}건</p>
            <p>TBM: {exportStatus.tbmCount}건</p>
          </div>
        )}
      </div>

    </div>
  );

  // Step 2b: 확인 화면
  const renderConfirmStep = () => (
    <div className="space-y-4">
      {/* 다운로드 가능 안내 (Direct인 경우) */}
      {downloadMethod === 'direct' && exportStatus?.canDownload && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-card">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-emerald-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm font-medium text-emerald-800">데이터 내보내기 완료!</span>
          </div>
          <div className="mt-2 text-sm text-emerald-700">
            <p>점검: {exportStatus.inspectionCount}건, 보고서: {exportStatus.reportCount}건, TBM: {exportStatus.tbmCount}건</p>
          </div>
          {/* 내보내기 완료 안내(초록 박스) 안의 확정 액션 — 의미색 유지 위해 success. */}
          <Button variant="success" onClick={handleDownload} className="mt-3">
            ZIP 파일 다운로드
          </Button>
        </div>
      )}

      {/* SMS/Email 선택 안내 */}
      {(downloadMethod === 'sms' || downloadMethod === 'email') && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-card">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="text-sm font-medium text-blue-800">
              {downloadMethod === 'sms' ? 'SMS로' : '이메일로'} 다운로드 링크가 발송됩니다
            </span>
          </div>
          <p className="mt-2 text-sm text-blue-700">
            데이터 내보내기가 완료되면 {downloadMethod === 'sms' ? '문자 메시지' : '이메일'}로 다운로드 링크를 보내드립니다.
            링크는 7일간 유효하며 최대 5회까지 다운로드할 수 있습니다.
          </p>
        </div>
      )}

      {/* 최종 확인 안내 */}
      <div className="p-4 bg-rose-50 border border-rose-200 rounded-card">
        <h4 className="text-sm font-medium text-rose-800 mb-2">탈퇴를 진행하시겠습니까?</h4>
        <p className="text-sm text-rose-700">
          탈퇴 후 30일 이내에 로그인하면 계정을 복구할 수 있습니다.
          30일이 지나면 모든 데이터가 영구 삭제됩니다.
        </p>
      </div>

      {error && (
        <div className="p-3 bg-rose-50 text-rose-600 text-sm rounded-card">{error}</div>
      )}

    </div>
  );

  // 내보낼 자료가 없는 경우의 최종 확인 화면
  //
  // "내려받을 자료가 없습니다" 같은 안내는 넣지 않는다 — 없는 것을 굳이 말할 이유가 없고,
  // 이 사람에게 필요한 정보는 "탈퇴하면 어떻게 되는가"뿐이다.
  const renderConfirmNoExportStep = () => (
    <form id={FORM_ID['confirm-no-export']} onSubmit={handleNoExportSubmit}>
      <div className="space-y-4">
        <div className="p-4 bg-rose-50 border border-rose-200 rounded-card">
          <h4 className="text-sm font-medium text-rose-800 mb-2">탈퇴를 진행하시겠습니까?</h4>
          <div className="text-sm text-rose-700 space-y-1">
            <p>회원 탈퇴 시 모든 조직 멤버십이 해제됩니다.</p>
            <p>
              탈퇴 후 <strong>30일 이내</strong>에 동일한 계정으로 로그인하면 계정을 복구할 수
              있습니다.
            </p>
            <p>30일이 지나면 계정과 데이터가 완전히 삭제됩니다.</p>
          </div>
        </div>

        {/* 다른 조직소유자가 있는 조직 안내 (org-action 단계를 안 거친 경우만) */}
        {!hasSoleOwnerOrg && nonSoleOwnerOrgs.length > 0 && (
          <Card padding="sm" surface="soft" elevation="none">
            <p className="text-sm text-ink-600">
              <span className="font-medium">
                {nonSoleOwnerOrgs.map((o) => o.company.name).join(', ')}
              </span>
              : 다른 {ROLE_LABELS.OWNER}가 있어 탈퇴 시 자동으로 멤버십만 해제됩니다.
            </p>
          </Card>
        )}

        {/* 비밀번호 확인 — 1단계를 건너뛰므로 신원 확인은 여기서 받는다 */}
        <div>
          <label
            htmlFor="password-no-export"
            className="block text-sm font-medium text-ink-700"
          >
            비밀번호 확인
          </label>
          <input
            type="password"
            id="password-no-export"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            // 치수·굵기는 request 단계의 비밀번호 칸과 동일 (탈퇴 컨텍스트라 포커스만 rose)
            className="mt-1 block w-full h-[42px] rounded-[10px] border border-rose-200 bg-white px-3 text-[14px] font-semibold text-ink-900 placeholder:text-ink-400 placeholder:font-medium focus:outline-none focus:border-rose-500 focus:ring-[3px] focus:ring-rose-100"
            placeholder="현재 비밀번호 입력"
            autoComplete="current-password"
          />
        </div>

        {error && (
          <div className="p-3 bg-rose-50 text-rose-600 text-sm rounded-card">{error}</div>
        )}
      </div>

    </form>
  );

  // Step 2c: 폐기 확인 화면
  const renderDiscardConfirmStep = () => (
    <div className="space-y-4">
      <div className="p-4 bg-orange-50 border border-orange-200 rounded-card">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-orange-800">데이터 폐기 안내</h3>
            <div className="mt-2 text-sm text-orange-700 space-y-2">
              <p>
                다운로드를 받지 않으시면 탈퇴 후 <strong>30일 이후</strong> 모든 데이터가 영구 삭제됩니다.
              </p>
              <div className="p-3 bg-orange-100 rounded-card">
                <p className="font-medium">법적 보존 기한 안내</p>
                <p className="mt-1">
                  산업안전보건법에 따라 점검 기록 및 보고서는 5년간 보관이 권장됩니다.
                  SafetyHub는 데이터 보관 의무가 없으므로 필요 시 직접 다운로드하여 보관하시기 바랍니다.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-rose-50 text-rose-600 text-sm rounded-card">{error}</div>
      )}

    </div>
  );

  // 현재 단계에 따른 제목
  const getModalTitle = () => {
    switch (step) {
      case 'org-action':
        return '조직 폐쇄 안내';
      case 'request':
        return '데이터 다운로드';
      case 'processing':
        return '데이터 준비 중';
      case 'confirm':
        return '탈퇴 확인';
      case 'discard-confirm':
        return '데이터 폐기 확인';
      case 'confirm-no-export':
        return '탈퇴 확인';
      default:
        return '회원 탈퇴';
    }
  };

  // org-action 로딩 중 표시
  const renderOrgActionLoading = () => (
    <div className="flex flex-col items-center justify-center py-8">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mb-4"></div>
      <p className="text-sm text-ink-500">조직 정보 확인 중...</p>
    </div>
  );

  /**
   * 단계별 푸터 — base `footer`는 모달당 **하나**라 단계 분기를 여기서 접는다(아래 본문 분기와
   * 짝이고, 순서도 같게 둔다). `type="submit"` 버튼은 그 단계의 폼 **밖**에 있으므로
   * `form={FORM_ID[단계]}`로 이어야 제출이 일어난다(없으면 조용히 아무 일도 하지 않는다).
   *
   * ⚠️ `org-action`은 **조회 중에는 푸터가 없다** — 종전에 그 푸터가 `renderOrgActionStep()`
   * 안에 있어 로딩 분기(`renderOrgActionLoading`)에서는 렌더되지 않았다. 그 성질을 유지한다.
   */
  const renderFooter = () => {
    if (step === 'org-action') {
      if (targetsLoading) return null;
      return (
        <>
          <Button variant="secondary" onClick={onClose}>
            취소
          </Button>
          {/* 폐쇄 외에 고를 것이 없으므로 게이트도 없다 — 이 단계는 확인만 받는다.
              단 대상 조회에 실패했으면 잠근다(그 사유는 본문 주석). */}
          <Button
            variant="primary"
            onClick={() => setStep(stepAfterOrgAction)}
            disabled={targetsFailed || targetsLoading || !exportTargets}
          >
            확인하고 계속
          </Button>
        </>
      );
    }
    if (step === 'request') {
      return (
        <>
          {hasSoleOwnerOrg ? (
            <Button variant="secondary" onClick={() => setStep('org-action')} disabled={isLoading}>
              이전
            </Button>
          ) : (
            <Button variant="secondary" onClick={onClose} disabled={isLoading}>
              취소
            </Button>
          )}
          <Button
            variant="danger"
            type="submit"
            form={FORM_ID.request}
            disabled={
              isLoading ||
              !password ||
              (downloadMethod === 'sms' && !accountPhone) ||
              (downloadMethod === 'email' && !accountEmail)
            }
          >
            {isLoading ? '처리 중...' : '탈퇴 요청'}
          </Button>
        </>
      );
    }
    if (step === 'processing') {
      return (
        <Button
          variant="secondary"
          onClick={() => {
            stopPolling();
            setStep('request');
          }}
        >
          취소
        </Button>
      );
    }
    if (step === 'confirm') {
      return (
        <>
          <Button variant="secondary" onClick={() => setStep('request')} disabled={isLoading}>
            이전
          </Button>
          <Button variant="danger" onClick={handleConfirmSubmit} disabled={isLoading}>
            {isLoading ? '처리 중...' : '회원 탈퇴'}
          </Button>
        </>
      );
    }
    if (step === 'discard-confirm') {
      return (
        <>
          {/* 파괴적 액션에 맞서는 권장 선택지 — 옅은 파랑 강조는 유지하고 치수만 Button에 넘긴다. */}
          <Button
            variant="secondary"
            onClick={() => {
              setDownloadMethod('direct');
              setStep('request');
            }}
            disabled={isLoading}
            className="border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-700"
          >
            다운로드 받기
          </Button>
          <Button variant="danger" onClick={handleConfirmSubmit} disabled={isLoading}>
            {isLoading ? '처리 중...' : '다운로드 없이 탈퇴'}
          </Button>
        </>
      );
    }
    if (step === 'confirm-no-export') {
      return (
        <>
          {hasSoleOwnerOrg ? (
            <Button
              variant="secondary"
              onClick={() => setStep('org-action')}
              disabled={isLoading}
            >
              이전
            </Button>
          ) : (
            <Button variant="secondary" onClick={onClose} disabled={isLoading}>
              취소
            </Button>
          )}
          <Button
            variant="danger"
            type="submit"
            form={FORM_ID['confirm-no-export']}
            disabled={isLoading || !password}
          >
            {isLoading ? '처리 중...' : '회원 탈퇴'}
          </Button>
        </>
      );
    }
    return null;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getModalTitle()}
      size="md"
      // 요청 중(탈퇴 요청·확인 제출)에도 닫는 길을 막는다 — 종전엔 처리 단계만 막아 제출 직후
      // Esc·✕로 닫히면 탈퇴는 서버에서 진행되는데 결과를 볼 화면이 사라졌다(리뷰 1103).
      preventClose={step === 'processing' || isLoading}
      footer={renderFooter()}
    >
      {step === 'org-action' && (targetsLoading ? renderOrgActionLoading() : renderOrgActionStep())}
      {step === 'request' && renderRequestStep()}
      {step === 'processing' && renderProcessingStep()}
      {step === 'confirm' && renderConfirmStep()}
      {step === 'discard-confirm' && renderDiscardConfirmStep()}
      {step === 'confirm-no-export' && renderConfirmNoExportStep()}
    </Modal>
  );
}
